import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Especies = () => {
  const [especies, setEspecies] = useState([]);
  const [originalEspecies, setOriginalEspecies] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedEspecie, setSelectedEspecie] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    nombre_especie: '',
    habilitada: true,
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet, id_rol y sucursales
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
        setUserSucursales(payload.sucursales || []);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de especie
  const normalizeEspecie = (especie) => {
    if (!especie || !especie.id_especie) {
    console.warn('Especie inválida recibida:', especie);
    return null;
    }

    // **CORRECCIÓN CLAVE:** Usar Boolean() para convertir 1/0 o true/false a un booleano estricto.
    const habilitadaValue = Boolean(especie.habilitada); 

    return {
    id_especie: especie.id_especie ?? null,
    id_pet: especie.id_pet ?? null,
    nombre_especie: especie.nombre_especie ?? '',
    habilitada: habilitadaValue, // Aplicamos el valor booleano garantizado
    createdAt: especie.createdAt ?? null,
    updatedAt: especie.updatedAt ?? null,
    };
    };

  // Obtener especies y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdRol) {
        console.log('Esperando id_pet o id_rol del usuario...');
        return;
      }
      try {
        setLoading(true);
        const especiesRes = await api.get('/especies/');
        console.log('Datos crudos de GET /especies:', especiesRes.data);
        const especiesData = Array.isArray(especiesRes.data)
          ? especiesRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const allowedPets = [userIdPet, ...userSucursales];
        const normalizedEspecies = especiesData
          .map(normalizeEspecie)
          .filter(especie => especie !== null && especie.id_especie && allowedPets.includes(especie.id_pet));
        console.log('Especies normalizadas:', normalizedEspecies);
        setEspecies(normalizedEspecies);
        setOriginalEspecies(normalizedEspecies);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        setPets(Array.isArray(petsRes.data) ? petsRes.data.filter(pet => allowedPets.includes(pet.id_pet)) : []);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdRol, userSucursales]);

  // Buscar especies en frontend
  useEffect(() => {
    const searchEspecies = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Especies originales:', originalEspecies);
      if (!searchTerm) {
        setEspecies(originalEspecies);
        console.log('Restaurando especies:', originalEspecies);
        return;
      }
      const filteredEspecies = originalEspecies
        .filter(especie => especie && especie.id_especie && especie.nombre_especie)
        .filter(especie => especie.nombre_especie.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('Especies filtradas:', filteredEspecies);
      setEspecies(filteredEspecies);
      setPage(0);
    };
    searchEspecies();
  }, [searchTerm, originalEspecies]);

  // Abrir modal para crear/editar especie
  const handleOpenModal = (especie = null) => {
    setSelectedEspecie(especie);
    setFormData(
      especie
        ? normalizeEspecie(especie) || {
            id_pet: userIdPet || '',
            nombre_especie: '',
            habilitada: true,
          }
        : {
            id_pet: userIdPet || '',
            nombre_especie: '',
            habilitada: true,
          }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar especie (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre_especie || !formData.id_pet) {
        const errorMsg = 'Nombre de la especie y veterinaria son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      const payload = {
        id_pet: formData.id_pet,
        nombre_especie: formData.nombre_especie,
        habilitada: formData.habilitada,
      };
      console.log('Guardando especie, payload:', payload);
      if (selectedEspecie) {
        const response = await api.put(`/especies/${selectedEspecie.id_especie}`, payload);
        console.log('Respuesta de PUT /especies:', response.data);
        const updatedEspecie = normalizeEspecie(response.data);
        if (updatedEspecie) {
          const newEspecies = especies.map(e => (e.id_especie === selectedEspecie.id_especie ? updatedEspecie : e));
          setEspecies(newEspecies);
          setOriginalEspecies(newEspecies);
          setSnackbarMessage('Especie actualizada exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/especies/', payload);
        console.log('Respuesta de POST /especies:', response.data);
        const newEspecie = normalizeEspecie(response.data);
        if (newEspecie) {
          const newEspecies = [...especies, newEspecie];
          setEspecies(newEspecies);
          setOriginalEspecies(newEspecies);
          setSnackbarMessage('Especie creada exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar especie:', error);
      const errorMsg = 'Error al guardar especie: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Deshabilitar especie
  const handleToggleHabilitada = async (id, habilitada) => {
    try {
      const especie = especies.find(e => e.id_especie === id);
      if (!especie) {
        throw new Error('Especie no encontrada en el estado local');
      }
      const payload = {
        id_pet: especie.id_pet,
        nombre_especie: especie.nombre_especie,
        habilitada: !habilitada,
      };
      console.log('Enviando PUT /especies/', id, 'con payload:', payload);
      const response = await api.put(`/especies/${id}`, payload);
      console.log('Respuesta de PUT /especies:', response.data);
      const updatedEspecie = normalizeEspecie(response.data, !habilitada);
      if (updatedEspecie) {
        const newEspecies = especies.map(e => (e.id_especie === id ? updatedEspecie : e));
        console.log('Nuevo estado de especies:', newEspecies);
        setEspecies(newEspecies);
        setOriginalEspecies(newEspecies);
        setSnackbarMessage(`Especie ${updatedEspecie.habilitada ? 'habilitada' : 'deshabilitada'} exitosamente`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('Error al actualizar estado:', error, 'Respuesta:', error.response);
      const errorMsg = 'Error al actualizar estado: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar especie
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta especie?')) return;
    try {
      console.log('Enviando DELETE /especies/', id);
      const response = await api.delete(`/especies/${id}`);
      console.log('Respuesta de DELETE /especies:', response.data);
      const newEspecies = especies.filter(e => e.id_especie !== id);
      setEspecies(newEspecies);
      setOriginalEspecies(newEspecies);
      setSnackbarMessage('Especie eliminada exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar especie:', error);
      const errorMsg = 'Error al eliminar especie: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedEspecies = especies.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Especies
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar especies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdRol ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : especies.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron especies. Crea una nueva especie para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva Especie
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nueva Especie
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de especies"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Nombre Especie
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Veterinaria
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Habilitada
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedEspecies.map((row) => {
                      if (!row || !row.id_especie) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_especie}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_especie}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.nombre_especie}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <Switch
                              checked={row.habilitada ?? false}
                              onChange={() => handleToggleHabilitada(row.id_especie, row.habilitada ?? false)}
                              color="primary"
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton
                              onClick={() => handleOpenModal(row)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            {/* ===================== Eliminar===================== 
                            <IconButton
                              onClick={() => handleDelete(row.id_especie)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                            */}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(especies.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(especies.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedEspecie ? 'Editar Especie' : 'Nueva Especie'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre de la Especie"
              fullWidth
              value={formData.nombre_especie}
              onChange={(e) => setFormData({ ...formData, nombre_especie: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.habilitada}
                onChange={(e) => setFormData({ ...formData, habilitada: e.target.checked })}
                color="primary"
              />
              <Typography>Habilitada</Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.nombre_especie || !formData.id_pet}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Especies;